import React, { useEffect, useState, useContext, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Grid } from '@material-ui/core';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import { Field, useFormikContext } from 'formik';
import { isMobile } from 'react-device-detect';

import { TextField } from 'Components/Fields';
import { FormSelect } from 'Components/Select';
import { HeadingFour, HeadingFive } from 'Components/Headings';
import { Separator } from 'Components/Separator';
import BackDateWarning from './BackDateWarning';
import { formatOptionName, isEmpty } from 'Helpers';
import { RESERVATION_STATUSES } from 'Queries/Admin/ReservationsStatuses';
import colors from 'Styles/Colors';
import { paragraphReg } from 'Styles/Typography';
import ProductCard from './ProductCard';
import StallSelection from './StallSelection';
import StallProductSelect from '../../shared/StallProductSelect';
import { initialValues } from '../Create';
import IncrementerIcon from 'Components/NumberIncrementer/IncrementerIcon';
import { QuestionAnswers } from '../../shared/ProductQuestionsCard';
import useLengthValidation from '../../shared/useLengthValidation';
import { useValidateAction } from 'Utils/actions';
import { actions } from 'Constants/actions';
import { UserContext } from 'Store/UserContext';
import { ADMIN, RESERVATION_ADMIN } from 'Constants/userRoles';
import Calendar from 'Components/Calendar/Calendar';
import { ReloadOutlined } from '@ant-design/icons';

const getStatusName = (statuses, stalls) => {
  if (isEmpty(stalls)) return '';
  const match = statuses.find(option => option.value === stalls.status) || null;
  return match;
};

const StallsBase = props => {
  const { className, setStallsOpen, setStallQuestionsAreValid } = props;
  const { values, setFieldValue, setValues, errors } = useFormikContext();
  const { event, reservationEdit, stalls } = values;
  const [isOpen, setIsOpen] = useState(false);
  const { data: reservationStatusesData, loading } = useQuery(RESERVATION_STATUSES);
  const updateStatus = useLengthValidation(event?.stallQuestions, setStallQuestionsAreValid);
  const { user } = useContext(UserContext);
  const isVenueAdmin = +user.role.id === ADMIN;
  const isReservationdmin = +user.role.id === RESERVATION_ADMIN;
  const isRoleValid = isVenueAdmin || isReservationdmin;
  const mapsEnabled = event?.venue?.interactiveMaps;
  const maps = event?.venue?.maps;

  const stallSelectionRef = useRef(null);
  const [isStallDataLoading, setIsStallDataLoading] = useState(false);

  // Initialize all required questions upfront when component mounts or questions change
  useEffect(() => {
    if (!isOpen || !event?.stallQuestions || event.stallQuestions.length === 0) return;

    // Check if required questions are enforced for admins (default: true)
    const requiredQuestionsForAdmins = event?.venue?.requiredQuestionsForAdmins ?? true;

    // If flag is off, clear any tracked questions and return
    if (!requiredQuestionsForAdmins) {
      if (Object.keys(values.hasEmptyStallQuestions || {}).length > 0) {
        setFieldValue('hasEmptyStallQuestions', {});
      }
      return;
    }

    const { productQuestionAnswers: questionAnswers, hasEmptyStallQuestions } = values;
    const currentRequiredQuestions = hasEmptyStallQuestions || {};
    const requiredQuestionIds = event.stallQuestions.filter(q => q.required).map(q => q.id);

    // Check which required questions don't have answers yet
    const unansweredRequiredQuestions = {};
    requiredQuestionIds.forEach(id => {
      const questionAnswer = questionAnswers.find(answer => answer.questionId == id);
      const question = event.stallQuestions.find(q => q.id == id);
      const hasShortField =
        (question?.questionType === 'multipleSelection' && questionAnswer?.answer?.some(ans => ans.replace('Other: ', '').length < 2)) ||
        (question?.questionType === 'openText' && questionAnswer?.answer?.[0]?.replace('Other: ', '').length < 1);

      const hasEmptyAnswer = !questionAnswer || !questionAnswer.answer?.length || hasShortField;
      if (hasEmptyAnswer) {
        unansweredRequiredQuestions[id] = true;
      }
    });

    // Only update if there are changes (to avoid infinite loops)
    const currentKeys = Object.keys(currentRequiredQuestions || {}).sort();
    const newKeys = Object.keys(unansweredRequiredQuestions).sort();
    const keysMatch = currentKeys.length === newKeys.length && currentKeys.every((key, index) => key === newKeys[index]);

    if (!keysMatch) {
      setFieldValue('hasEmptyStallQuestions', unansweredRequiredQuestions);
    }
  }, [isOpen, event?.stallQuestions, event?.venue?.requiredQuestionsForAdmins, values.productQuestionAnswers, values.hasEmptyStallQuestions, setFieldValue]);

  useEffect(() => {
    setStallsOpen(isOpen);
    if (!isOpen && !reservationEdit) {
      setValues({
        ...values,
        stalls: initialValues.stalls,
        stallProductId: initialValues.stallProductId,
        selectedStalls: initialValues.selectedStalls,
        sameDates: false
      });
    }
  }, [isOpen]);

  // PRELOAD SVG MAPS
  useEffect(() => {
    if (mapsEnabled && maps?.length > 0) {
      maps.forEach(map => fetch(map.url));
    }
  }, [mapsEnabled, maps]);

  const today = moment().startOf('days');
  const isBackDateSelected = values.stalls.start?.isBefore(today);
  const eventHasStarted = moment().isAfter(moment(event.startDate).startOf('days'));
  if (loading) {
    return <></>;
  }

  let reservationStatuses = [];
  if (reservationStatusesData) {
    reservationStatuses = reservationStatusesData.reservationStatuses.reduce((acc, status) => {
      if (Number(status.id) !== 4) {
        acc.push({ value: status.id, label: formatOptionName(status.name) });
      }
      return acc;
    }, []);
  }

  const setDateRange = dates => {
    setFieldValue('stalls', {
      ...values.stalls,
      start: dates.startDate,
      end: dates.endDate
    });
  };

  const statusName = getStatusName(reservationStatuses, stalls);
  const statusOptions = {
    cb: e => setFieldValue('stalls.status', e.target.value),
    key: statusName ? statusName.value : null,
    label: 'STATUS',
    options: reservationStatuses,
    selectedOption: stalls && stalls.status ? stalls.status : '1',
    type: 'select',
    value: statusName
  };

  const quantityChange = async newValue => {
    let value = newValue;
    await setFieldValue('stalls.quantity', Number(value) <= 0 ? '' : value.replace(/[^0-9]/g, ''));
  };

  const setIncludeStallsSwitch = isOpenValue => {
    setIsOpen(isOpenValue);
    setFieldValue('isBelowMinNights.stalls', false);

    if (!isOpenValue) {
      setFieldValue('hasEmptyStallQuestions', {});
    }
  };

  const handleRefreshClick = () => {
    if (stallSelectionRef.current) {
      stallSelectionRef.current.refetchStalls();
    } else {
      console.warn('StallSelection ref not available yet.');
    }
  };

  const canAssignStalls = useValidateAction('orders', actions.PRODUCT_ASSIGNMENT);
  const canChangeStallsStatus = useValidateAction('orders', actions.RV_STATUS_DROPDOWN_ON_RESERVATION_SIDEBAR);

  return (
    <StallsCard
      isMobile={isMobile}
      className={`${className} ${isMobile ? 'mobile' : ''} ${isStallDataLoading ? 'loading' : ''}`}
      edit={reservationEdit}
      event={event}
      isOpen={isOpen}
      productType={'stallProduct'}
      setIsOpen={setIncludeStallsSwitch}>
      <div className={`card-content ${isOpen ? `open` : ''}`}>
        {isOpen ? (
          <>
            <Separator margin="0.25rem 0 1.375rem" />
            <Grid container className="qty-and-date">
              <Grid item direction="column" md={6} className="quantity-column">
                <HeadingFive label={'Quantity'} />
                <Field name={'stalls.quantity'}>
                  {({ field, meta }) => (
                    <div className="quantity-field-wrapper">
                      <IncrementerIcon
                        increment={async () => {
                          let value = Number(field.value) + 1;
                          quantityChange(value.toString());
                        }}
                        decrement={async () => {
                          let value = Number(field.value) - 1;
                          quantityChange(value.toString());
                        }}
                        top={29}
                        right={13}
                      />
                      <TextField
                        {...field}
                        {...meta}
                        autoComplete={'numberOfStalls'}
                        className="stalls-field"
                        disabled={isEmpty(event) || reservationEdit}
                        error={Boolean(meta.error)}
                        helperText={!!meta.error && meta.error}
                        inputProps={{ maxLength: 3 }}
                        InputLabelProps={{ shrink: field.value > 0 }}
                        label={'NUMBER OF STALLS'}
                        onChange={e => quantityChange(e.target.value)}
                        type={'number'}
                        variant={'filled'}
                      />
                    </div>
                  )}
                </Field>
              </Grid>
              <Grid item direction="column" md={6} className="date-column">
                <div className="date-wrapper">
                  <HeadingFive label={'Dates'} />
                  <div className={`date-input--wrapper ${values.isBelowMinNights.stalls && 'error'}`}>
                    <Calendar
                      selectableRangeStart={moment(event.startDate).format('YYYY-MM-DD')}
                      selectableRangeEnd={event.endDate}
                      startDatePlaceholder={'CHECK IN'}
                      endDatePlaceholder={'CHECK OUT'}
                      plural={true}
                      disabled={!stalls || reservationEdit || !!errors.stalls}
                      backDate={isRoleValid ? moment(event.startDate).format('YYYY-MM-DD') : null}
                      cb={({ startDate, endDate }) => {
                        setDateRange({ startDate, endDate });
                      }}
                      styles={{
                        colorBgContainer: '#f1f4f7',
                        colorBorder: 'transparent'
                      }}
                    />
                    <span className="error-msg">Min Nights is {values.selectedStallMinNights}</span>
                  </div>
                  {isBackDateSelected && <BackDateWarning />}
                </div>
              </Grid>
            </Grid>
            {canChangeStallsStatus && (
              <>
                <HeadingFive label={'Stall Reservation Status'} className="spacing-top" />
                <FormSelect disabled={!!errors.stalls} className="status-options" {...statusOptions} />
              </>
            )}
            <HeadingFour label={'Stall Rate Type'} className="spacing-bottom" />
            <StallProductSelect className={className} noLayout allowBackDateQuery={isBackDateSelected && eventHasStarted} />
            {canAssignStalls && (
              <>
                <div className="stall-assignment-wrapper">
                  <HeadingFour label={'Stall Assignment'} className="" />
                  {isRoleValid && !!values.stallProductId && (
                    <span title="Refresh" onClick={handleRefreshClick} className="refresh-icon-wrapper">
                      <ReloadOutlined className={isStallDataLoading ? 'spinning' : ''} style={{ fontSize: '20px', color: '#2875c3' }} />
                    </span>
                  )}
                </div>
                {!!values.stallProductId && (
                  <StallSelection ref={stallSelectionRef} onLoadingChange={setIsStallDataLoading} mapsEnabled={mapsEnabled} maps={maps} />
                )}
                {event.stallQuestions.length > 0 && (
                  <>
                    <Separator margin="20px 0" />
                    <HeadingFour label={'Additional Stall Info'} className="" />
                  </>
                )}
              </>
            )}
            {event.stallQuestions.map((question, index) => (
              <>
                <p className={question.questionType === 'openText' ? 'open-text' : ''}>
                  {question.question}
                  {question.required && <span className="required-text"> (Required)</span>}
                </p>
                <QuestionAnswers question={question} productType="stalls" updateStatus={updateStatus} index={index} />
              </>
            ))}
          </>
        ) : (
          <></>
        )}
      </div>
    </StallsCard>
  );
};

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const StallsCard = styled(ProductCard)`
  .spacing-top {
    margin-top: 20px !important;
  }

  .qty-and-date {
    align-items: center;
  }

  &.mobile {
    .qty-and-date {
      flex-direction: column;
    }

    .quantity-column {
      padding-right: 0;
      width: 100%;
    }

    .date-column {
      padding-left: 0;
      width: 100%;
    }
  }

  .spacing-bottom {
    margin-bottom: 20px !important;
  }

  .MuiSwitch-colorPrimary.Mui-checked {
    color: ${colors.white};
  }

  .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track {
    background-color: ${colors.button.primary.active};
    opacity: 1;
  }

  .status-options {
    ${paragraphReg}
    color: rgb(0, 0, 0);
    font-size: 0.9375rem;
    line-height: 24px;
    margin: 10px 0 20px;
    padding: 13px 0 0;
    background-color: ${colors.background.primary} !important;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;

    &::placeholder {
      color: ${colors.text.secondary};
    }

    &--disabled {
      opacity: 0.5;
    }

    label[class^='MuiInputLabel-formControl'],
    label[class*='MuiInputLabel-formControl'] {
      top: -13px !important;
    }

    svg[class^='MuiSelect-icon'],
    svg[class*='MuiSelect-icon'] {
      top: -2px;
    }

    div[class^='MuiSelect-selectMenu'],
    div[class*='MuiSelect-selectMenu'] {
      margin-bottom: 15px;
    }
  }

  .date-wrapper {
    h5 {
      margin-bottom: 18px !important;
    }
  }

  .date-input--wrapper {
    height: 56px;
    margin-bottom: 26px;

    .calendar-picker {
      width: 100%;
      height: 100%;
      margin-top: 5px;
    }

    .error-msg {
      display: none;
      color: #f44336;
      margin-top: 13px;
    }

    &.error {
      border-bottom: 2px solid #f44336;

      .error-msg {
        display: block;
      }
    }
  }

  .quantity-column {
    padding-right: 10px;
  }

  .date-column {
    padding-left: 10px;
  }

  .quantity-field-wrapper {
    position: relative;
  }

  .stall-assignment-wrapper {
    display: flex;
    align-items: center;

    .refresh-icon-wrapper {
      cursor: pointer;
      margin-left: 10px;
      line-height: 0;
      display: inline-flex;
    }

    .spinning {
      animation: ${spin} 1s linear infinite;
    }
  }
`;

export default StallsBase;
